import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { destroyCategoryAsync, fetchCategoriesAsync, selectCategories } from './categorySlice';
import { Box, Button, Container, Paper, Stack } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColumns, GridRowParams } from '@mui/x-data-grid';
import CategoryDialog from './CategoryDialog';
import { Delete, Edit } from '@mui/icons-material';
import { DialogState } from '../../../../app/types';
import { setDialog, setPageName } from '../../../../app/appSlice';
import { useNavigate } from 'react-router-dom';
import { Category } from '../../../../generated/api';

const CategoryView = () => {
  const dispatch = useAppDispatch();
  const categoryState = useAppSelector(selectCategories);
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  useEffect(() => {
    dispatch(setPageName('Categories'));
    dispatch(fetchCategoriesAsync());
  }, []);

  const deleteCategory = (category: Category) => () => {
    const dialog: DialogState = {
      open: true,
      title: `Are you sure you want to delete Category "${category.name}"?`,
      description: 'This action cannot be undone.',
      onOk: () => dispatch(destroyCategoryAsync(category.id))
    };
    dispatch(setDialog(dialog));
  };

  const editCategory = (category: Category) => () => {
    setSelectedCategory(category);
    setDialogOpen(true);
  };

  const closeCategoryDialog = () => {
    setSelectedCategory(null);
    setDialogOpen(false);
  };

  const columns = useMemo<GridColumns<Category>>(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, headerAlign: 'center', align: 'center' },
      { field: 'description', headerName: 'Description', flex: 3, headerAlign: 'center', align: 'center' },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem key={1} label="edit" icon={<Edit />} onClick={editCategory(params.row)} />,
          <GridActionsCellItem key={2} label="delete" icon={<Delete />} onClick={deleteCategory(params.row)} />
        ]
      }
    ],
    [editCategory, deleteCategory]
  );

  return (
    <Box marginTop={1} minWidth="100%">
      <Container maxWidth="lg">
        <Paper>
          <Stack spacing={1} padding={1}>
            <Box display="flex" justifyContent="center">
              <Button onClick={() => setDialogOpen(true)} variant="contained">
                New category
              </Button>
            </Box>
            <DataGrid
              isRowSelectable={() => false}
              autoHeight
              hideFooter
              columns={columns}
              getRowHeight={() => 'auto'}
              rows={categoryState.categories}
              loading={categoryState.loading}
              sx={{
                boxShadow: 0,
                border: 0,
                '&.MuiDataGrid-root .MuiDataGrid-cell': { py: 1 }
              }}
              getRowId={(row) => row.id}
              onRowDoubleClick={(row) => navigate(`${row.id}`)}
            />
          </Stack>
        </Paper>
      </Container>
      <CategoryDialog open={dialogOpen} close={closeCategoryDialog} category={selectedCategory} />
    </Box>
  );
};

export default CategoryView;
